import React, { useState, useEffect } from "react";
import { firebaseApp, db } from "@healthdesk/shared-services";
import firebase from "firebase/compat/app";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@healthdesk/shared-hooks";

const Wrapper = ({ children }) => (
  <Box sx={{ textAlign: "center", maxWidth: "sm", m: "auto" }}>{children}</Box>
);

const Verify = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [verified, setVerified] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState("");

  const navigate = useNavigate();

  // Get parameters from the URL
  const getParameter = (parameter) => {
    const matches = new RegExp(`${parameter}=([^&#=]*)`).exec(
      window.location.search
    );
    if (matches) return matches[1];

    return undefined;
  };

  const handleVerifyEmail = async () => {
    const code = getParameter("oobCode");
    const continueUrlParam = getParameter("continueUrl");

    if (!code) {
      console.error("Missing oobCode parameter");
      setLoading(false);
      setError(true);
      setErrorMessage("Missing verification code in the URL");
      return;
    }


    // Extract uid and pendingEmail from continueUrl (works even if user isn't logged in)
    let uidFromUrl = null;
    let pendingEmailFromUrl = null;

    if (continueUrlParam) {
      try {
        const continueUrl = new URL(decodeURIComponent(continueUrlParam));
        const continueParams = new URLSearchParams(continueUrl.search);
        uidFromUrl = continueParams.get("uid");
        pendingEmailFromUrl = continueParams.get("pendingEmail");
      } catch (err) {
        console.error("Error parsing continueUrl:", err);
      }
    }

    try {
      // Apply the verification code - this updates Firebase Auth
      await firebaseApp.auth().applyActionCode(code);

      setVerified(true);

      // Get the UID - prefer URL param (works even when not logged in), fallback to authenticated user
      const uid = uidFromUrl || user?.uid;

      if (!uid) {
        console.error("No UID available from URL or authenticated user");
        setVerificationMessage(
          "Your email has been verified! Please sign in to continue."
        );
        setLoading(false);
        return;
      }

      // Update Firestore directly with the verified email
      try {
        const userRef = db.collection("users").doc(uid);
        const userDoc = await userRef.get();

        if (userDoc.exists) {
          const userData = userDoc.data();
          const newEmail = pendingEmailFromUrl || userData.pendingEmail;

          if (newEmail) {
            await userRef.update({
              email: newEmail,
              pendingEmail: firebase.firestore.FieldValue.delete(),
              emailChangeRequested: firebase.firestore.FieldValue.delete(),
            });

            setVerificationMessage(
              `Your email has been successfully verified as ${newEmail}. You can now use all features that require a verified email.`
            );
          } else {
            setVerificationMessage(
              "Your email has been verified! Please sign in to continue."
            );
          }
        } else {
          setVerificationMessage(
            "Your email has been verified! Please sign in to continue."
          );
        }
      } catch (firestoreError) {
        console.error("Error updating Firestore:", firestoreError);
        setVerificationMessage(
          "Your email has been verified, but we encountered an error updating your profile. The change will sync automatically when you sign in."
        );
      }

      setLoading(false);
    } catch (error) {
      console.error("Action code error:", error);
      setLoading(false);

      // Check if the error is because code was already used (email might already be verified)
      if (error.code === "auth/invalid-action-code") {
        // The code might have been used already - check if email is already set
        const uid = uidFromUrl || user?.uid;
        if (uid && pendingEmailFromUrl) {
          try {
            const userDoc = await db.collection("users").doc(uid).get();
            if (
              userDoc.exists &&
              userDoc.data().email === pendingEmailFromUrl
            ) {
              // Email is already verified!
              setVerified(true);
              setVerificationMessage(
                `Your email (${pendingEmailFromUrl}) is already verified!`
              );
              return;
            }
          } catch (e) {
            console.error("Error checking existing email:", e);
          }
        }
      }

      setError(true);
      setErrorMessage(`Verification failed: ${error.message}`);
    }
  };

  // NOTE: Email sign-in handler commented out - may be needed for debugging
  // Uncomment if client wants email login option back
  /*
  const handleSignIn = async () => {
    try {
      if (!firebaseApp.auth().isSignInWithEmailLink(window.location.href)) {
        setLoading(false);
        setError(true);
        setErrorMessage("Invalid sign-in link.");
        return;
      }

      const email = localStorage.getItem("emailForSignIn");
      if (!email) {
        navigate("/onboarding?needEmail=true");
        return;
      }

      const result = await firebaseApp
        .auth()
        .signInWithEmailLink(email, window.location.href);
      const userDoc = await db.collection("users").doc(result.user.uid).get();

      localStorage.removeItem("emailForSignIn");

      if (!userDoc.exists) {
        navigate("/onboarding");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Sign-in error:", error);
      setLoading(false);
      setError(true);
      setErrorMessage(error.message);
    }
  };
  */

  useEffect(() => {
    const mode = getParameter("mode");

    if (mode === "verifyEmail" || mode === "verifyAndChangeEmail") {
      handleVerifyEmail();
    } else {
      // Unknown action type - redirect to dashboard
      navigate("/dashboard");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading)
    return (
      <Wrapper>
        <CircularProgress sx={{ mt: 8 }} />
      </Wrapper>
    );

  if (error) {
    return (
      <Wrapper>
        <Typography variant="h4" sx={{ mt: { xs: 4, sm: 10 }, mb: 2 }}>
          Verification Failed
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {errorMessage ||
            "Your verification link has expired or has already been used. Please request a new verification email."}
        </Typography>

        <Button variant="contained" component={Link} to="/dashboard">
          Return to Dashboard
        </Button>
      </Wrapper>
    );
  }

  // Email verification success
  if (verified) {
    return (
      <Wrapper>
        <Typography variant="h4" sx={{ mt: { xs: 4, sm: 10 }, mb: 2 }}>
          Email Verified!
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {verificationMessage}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          For security reasons, you may need to sign in again after changing
          your email.
        </Typography>

        <Button variant="contained" component={Link} to="/auth">
          Continue
        </Button>
      </Wrapper>
    );
  }

  return null;
};

export default Verify;
